import { useCallback, useRef, useState } from 'react';
import { type EditCommand, type Edl, History } from '@repo/editor-core';
import { editErrorMessage } from './edit-error-ko';

export interface EditorState {
  edl: Edl;
  /** Dispatch a command through the bus. Returns the error message on rejection. */
  dispatch: (cmd: EditCommand) => string | null;
  undo: () => void;
  redo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  /** True when the EDL differs from the last marked-saved snapshot. */
  dirty: boolean;
  markSaved: () => void;
  /** 서버가 돌려준 EDL로 교체 (저장 응답이 자막을 재생성했을 때). */
  replaceSaved: (next: Edl) => void;
}

/**
 * Wraps editor-core's snapshot {@link History}. ALL edits flow through
 * `dispatch` → the command bus → reducer; the UI never mutates the EDL directly.
 */
export function useEditorState(initial: Edl): EditorState {
  const historyRef = useRef<History | null>(null);
  if (historyRef.current === null) historyRef.current = new History(initial);
  const history = historyRef.current;

  const [edl, setEdl] = useState<Edl>(initial);
  const savedRef = useRef<Edl>(initial);
  const [, bump] = useState(0);

  const sync = useCallback(() => {
    setEdl(history.current);
    bump((n) => n + 1);
  }, [history]);

  const dispatch = useCallback(
    (cmd: EditCommand): string | null => {
      try {
        history.dispatch(cmd);
        sync();
        return null;
      } catch (e) {
        // 리듀서 원문은 영문 + 내부 id 다 — 사용자에게는 코드별 한국어로 바꿔 준다.
        return editErrorMessage(e);
      }
    },
    [history, sync]
  );

  const undo = useCallback(() => {
    history.undo();
    sync();
  }, [history, sync]);
  const redo = useCallback(() => {
    history.redo();
    sync();
  }, [history, sync]);
  /** 서버가 돌려준 EDL로 교체하고 저장 상태로 표시한다 (저장 응답 동기화). */
  const replaceSaved = useCallback(
    (next: Edl) => {
      history.replace(next);
      savedRef.current = history.current;
      sync();
    },
    [history, sync]
  );
  const markSaved = useCallback(() => {
    savedRef.current = history.current;
    bump((n) => n + 1);
  }, [history]);

  return {
    edl,
    dispatch,
    undo,
    redo,
    canUndo: history.canUndo,
    canRedo: history.canRedo,
    dirty: edl !== savedRef.current,
    markSaved,
    replaceSaved,
  };
}
